import React, { useState, useMemo } from 'react'
import { motion } from 'framer-motion'
import { useFamily } from '../../contexts/FamilyContext'
import { useAuth } from '../../contexts/AuthContext'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Card from '../ui/Card'
import Badge from '../ui/Badge'
import Button from '../ui/Button'

const { 
  FiTrendingUp, FiCalendar, FiAward, FiUsers, FiStar, 
  FiBarChart3, FiPieChart, FiTarget, FiGift, FiClock,
  FiTrendingDown, FiActivity, FiFilter
} = FiIcons

const RewardAnalytics = () => {
  const { redemptions, rewards, completions } = useFamily()
  const { family } = useAuth()
  const [timeFilter, setTimeFilter] = useState('all') // all, week, month, quarter
  const [childFilter, setChildFilter] = useState('all')

  // Filter data based on selected time period
  const filteredRedemptions = useMemo(() => {
    if (timeFilter === 'all') return redemptions

    const now = new Date()
    let startDate = new Date()

    switch (timeFilter) {
      case 'week':
        startDate.setDate(now.getDate() - 7)
        break
      case 'month':
        startDate.setMonth(now.getMonth() - 1)
        break
      case 'quarter':
        startDate.setMonth(now.getMonth() - 3)
        break
      default:
        return redemptions
    }

    return redemptions.filter(redemption => 
      new Date(redemption.redeemed_at) >= startDate &&
      (childFilter === 'all' || redemption.child_id === childFilter)
    )
  }, [redemptions, timeFilter, childFilter])

  // Calculate analytics
  const analytics = useMemo(() => {
    const totalRedemptions = filteredRedemptions.length
    const totalPointsSpent = filteredRedemptions.reduce((sum, r) => sum + (r.points_spent || 0), 0)
    const uniqueChildren = new Set(filteredRedemptions.map(r => r.child_id)).size
    const avgPointsPerRedemption = totalRedemptions > 0 ? Math.round(totalPointsSpent / totalRedemptions) : 0

    // Most popular rewards
    const rewardCounts = {}
    filteredRedemptions.forEach(redemption => {
      const rewardId = redemption.reward_id
      rewardCounts[rewardId] = (rewardCounts[rewardId] || 0) + 1
    })

    const popularRewards = Object.entries(rewardCounts)
      .map(([rewardId, count]) => {
        const reward = rewards.find(r => r.id === rewardId)
        return { reward, count }
      })
      .filter(item => item.reward)
      .sort((a, b) => b.count - a.count)
      .slice(0, 5)

    // Child performance
    const childStats = {}
    if (family?.children) {
      family.children.forEach(child => {
        const childRedemptions = filteredRedemptions.filter(r => r.child_id === child.id)
        const childCompletions = completions.filter(c => c.child_id === child.id)
        
        childStats[child.id] = {
          child,
          redemptions: childRedemptions.length,
          pointsSpent: childRedemptions.reduce((sum, r) => sum + (r.points_spent || 0), 0),
          pointsEarned: childCompletions.reduce((sum, c) => sum + (c.points_earned || 0), 0),
          lastRedemption: childRedemptions.length > 0 
            ? new Date(Math.max(...childRedemptions.map(r => new Date(r.redeemed_at))))
            : null
        }
      })
    }

    // Weekly trend (last 8 weeks)
    const weeklyTrend = []
    for (let i = 7; i >= 0; i--) {
      const weekStart = new Date()
      weekStart.setDate(weekStart.getDate() - (i * 7))
      const weekEnd = new Date(weekStart)
      weekEnd.setDate(weekEnd.getDate() + 6)

      const weekRedemptions = redemptions.filter(r => {
        const redemptionDate = new Date(r.redeemed_at)
        return redemptionDate >= weekStart && redemptionDate <= weekEnd
      })

      weeklyTrend.push({
        week: `${weekStart.getMonth() + 1}/${weekStart.getDate()}`,
        redemptions: weekRedemptions.length,
        points: weekRedemptions.reduce((sum, r) => sum + (r.points_spent || 0), 0)
      })
    }

    return {
      totalRedemptions,
      totalPointsSpent,
      uniqueChildren,
      avgPointsPerRedemption,
      popularRewards,
      childStats,
      weeklyTrend
    }
  }, [filteredRedemptions, rewards, completions, family])

  const timeFilterOptions = [
    { value: 'all', label: 'All Time' },
    { value: 'week', label: 'Last Week' },
    { value: 'month', label: 'Last Month' },
    { value: 'quarter', label: 'Last 3 Months' }
  ]

  return (
    <div className="space-y-6">
      {/* Header with Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Reward Analytics</h2>
          <p className="text-gray-600 dark:text-gray-400">Track reward redemptions and family motivation</p>
        </div>
        
        <div className="flex gap-3">
          <select
            value={timeFilter}
            onChange={(e) => setTimeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            {timeFilterOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {family?.children && family.children.length > 1 && (
            <select
              value={childFilter}
              onChange={(e) => setChildFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              <option value="all">All Children</option>
              {family.children.map(child => (
                <option key={child.id} value={child.id}>
                  {child.name}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 dark:text-gray-400 text-sm">Total Redemptions</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                {analytics.totalRedemptions}
              </p>
            </div>
            <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-full">
              <SafeIcon icon={FiGift} className="text-2xl text-primary-600 dark:text-primary-400" />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 dark:text-gray-400 text-sm">Points Spent</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                {analytics.totalPointsSpent.toLocaleString()}
              </p>
            </div>
            <div className="p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-full">
              <SafeIcon icon={FiStar} className="text-2xl text-yellow-600 dark:text-yellow-400" />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 dark:text-gray-400 text-sm">Active Children</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                {analytics.uniqueChildren}
              </p>
            </div>
            <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-full">
              <SafeIcon icon={FiUsers} className="text-2xl text-green-600 dark:text-green-400" />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 dark:text-gray-400 text-sm">Avg Points/Reward</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                {analytics.avgPointsPerRedemption}
              </p>
            </div>
            <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-full">
              <SafeIcon icon={FiTarget} className="text-2xl text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Weekly Trend */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Weekly Trend</h3>
            <SafeIcon icon={FiTrendingUp} className="text-primary-600 dark:text-primary-400" />
          </div>
          
          <div className="space-y-3">
            {analytics.weeklyTrend.map((week, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">{week.week}</span>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1">
                    <div 
                      className="h-2 bg-primary-500 rounded-full"
                      style={{ width: `${Math.max(week.redemptions * 20, 4)}px` }}
                    />
                    <span className="text-sm font-medium">{week.redemptions}</span>
                  </div>
                  <Badge variant="gray" size="sm">{week.points} pts</Badge>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Popular Rewards */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Most Popular Rewards</h3>
            <SafeIcon icon={FiAward} className="text-yellow-600 dark:text-yellow-400" />
          </div>
          
          <div className="space-y-3">
            {analytics.popularRewards.length > 0 ? (
              analytics.popularRewards.map((item, index) => (
                <div key={item.reward.id} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-lg">{item.reward.emoji || '🎁'}</span>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {item.reward.title}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {item.reward.points_required} points
                      </p>
                    </div>
                  </div>
                  <Badge variant="primary">{item.count}x</Badge>
                </div>
              ))
            ) : (
              <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                No redemptions yet
              </p>
            )}
          </div>
        </Card>
      </div>

      {/* Child Performance */}
      {family?.children && family.children.length > 0 && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Child Performance</h3>
            <SafeIcon icon={FiUsers} className="text-blue-600 dark:text-blue-400" />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {family.children.map(child => {
              const stats = analytics.childStats[child.id] || {}
              const efficiency = stats.pointsEarned > 0 
                ? Math.round((stats.pointsSpent / stats.pointsEarned) * 100)
                : 0

              return (
                <motion.div
                  key={child.id}
                  className="p-4 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 rounded-lg"
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="text-2xl">{child.avatar || '👦'}</div>
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100">{child.name}</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Age {child.age || 'Unknown'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-gray-600 dark:text-gray-400">Redemptions</p>
                      <p className="font-bold text-primary-600 dark:text-primary-400">
                        {stats.redemptions || 0}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600 dark:text-gray-400">Points Spent</p>
                      <p className="font-bold text-red-600 dark:text-red-400">
                        {stats.pointsSpent || 0}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600 dark:text-gray-400">Points Earned</p>
                      <p className="font-bold text-green-600 dark:text-green-400">
                        {stats.pointsEarned || 0}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600 dark:text-gray-400">Efficiency</p>
                      <p className="font-bold text-purple-600 dark:text-purple-400">
                        {efficiency}%
                      </p>
                    </div>
                  </div>

                  {stats.lastRedemption && (
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                        <SafeIcon icon={FiClock} />
                        <span>
                          Last reward: {stats.lastRedemption.toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  )}
                </motion.div>
              )
            })}
          </div>
        </Card>
      )}

      {/* Insights */}
      {analytics.totalRedemptions > 0 && (
        <Card className="p-6 bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-primary-900/20 dark:to-secondary-900/20">
          <div className="flex items-center gap-3 mb-4">
            <SafeIcon icon={FiActivity} className="text-2xl text-primary-600 dark:text-primary-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Insights & Recommendations</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {analytics.avgPointsPerRedemption > 50 && (
              <div className="p-3 bg-white dark:bg-gray-800 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <SafeIcon icon={FiTrendingUp} className="text-green-500" />
                  <span className="font-medium text-green-700 dark:text-green-400">High Value Rewards</span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Your children are saving up for bigger rewards! Consider adding more medium-point options.
                </p>
              </div>
            )}

            {analytics.uniqueChildren < (family?.children?.length || 0) && (
              <div className="p-3 bg-white dark:bg-gray-800 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <SafeIcon icon={FiUsers} className="text-orange-500" />
                  <span className="font-medium text-orange-700 dark:text-orange-400">Engage All Children</span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Some children haven't redeemed rewards yet. Consider age-appropriate options for everyone.
                </p>
              </div>
            )}

            {analytics.popularRewards.length > 0 && analytics.popularRewards[0].count > 3 && (
              <div className="p-3 bg-white dark:bg-gray-800 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <SafeIcon icon={FiAward} className="text-purple-500" />
                  <span className="font-medium text-purple-700 dark:text-purple-400">Popular Choice</span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  "{analytics.popularRewards[0].reward.title}" is very popular! Consider similar rewards.
                </p>
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  )
}

export default RewardAnalytics